import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import {
  ContactEmailInformation,
  SendQuestionInformation,
  ContactInformation,
  GetContactInformation,
  SecurityQuestionChange,
  UserNamePasswordChange
} from '../models/contact';

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('ContactService');
  }

  public getContactEmailInformation(participantId: number): Observable<ContactEmailInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/GetContactEmailInformation';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.getContactEmailInformation :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<ContactEmailInformation>(serviceUrl, {participantId: participantId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getContactEmailInformation', null))
    );
  }

  public sendQuestion(sendQuestionInformation: SendQuestionInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/SendQuestion';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.sendQuestion :: serviceUrl = ' + serviceUrl);
    }

    // return value of 0 equals success
    return this._httpClient.post<number>(serviceUrl, sendQuestionInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('sendQuestion', null))
    );
  }

  public getContactInformation(getContactInformation: GetContactInformation): Observable<ContactInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/GetContactInformation';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.getContactInformation :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<ContactInformation>(serviceUrl, getContactInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getContactInformation', null))
    );
  }

  public getNewSecurityQuestionChange(): SecurityQuestionChange {
    const newSecurityQuestionChange: SecurityQuestionChange = {
      userName: null,
      userId: null,
      securityQuestionId: null,
      securityQuestionText: null,
      securityQuestionAnswer: null,
      participantId: null
    };
    return newSecurityQuestionChange;
  }

  public getSecurityQuestionData(securityQuestionChangeSend: SecurityQuestionChange): Observable<SecurityQuestionChange> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/GetSecurityQuestion';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.getSecurityQuestion :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<SecurityQuestionChange>(serviceUrl, securityQuestionChangeSend).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getSecurityQuestion', null))
    );
  }

  public updateContactInformation(contactInformation: ContactInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/ContactInformation/Update';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.updateContactInformation :: serviceUrl = ' + serviceUrl);
    }

    // return value of 0 equals success
    return this._httpClient.post<number>(serviceUrl, contactInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('updateContactInformation', null))
    );
  }

  public updatePassword(userNamePasswordChange: UserNamePasswordChange): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/UserNamePasswordChange';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.updatePassword :: serviceUrl = ' + serviceUrl);
    }

    // return value of 0 equals success
    return this._httpClient.post<number>(serviceUrl, userNamePasswordChange).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('updatePassword', null))
    );
  }

  public updateSecurityQuestion(securityQuestionChange: SecurityQuestionChange): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/UpdateSecurityQuestion';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.updateSecurityQuestion :: serviceUrl = ' + serviceUrl);
    }

    // return value of greater than 0 equals success
    return this._httpClient.post<number>(serviceUrl, securityQuestionChange).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('updateSecurityQuestion', null))
    );
  }

  public updateSecurityQuestionLogged(securityQuestionChange: SecurityQuestionChange): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Contact/UpdateSecurityQuestionLogged';

    if (this._logging.serviceCallTraceLog) {
      console.log('ContactService.UpdateSecurityQuestionLogged :: serviceUrl = ' + serviceUrl);
    }

    // return value of 0 equals success
    return this._httpClient.post<number>(serviceUrl, securityQuestionChange).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('UpdateSecurityQuestionLogged', null))
    );
  }
}
